import { createAsyncThunk, createSlice, isAnyOf, } from '@reduxjs/toolkit'
import { AppState } from '@app/store/state/app.state';
import {
  getMovementsByTags,
  requestTeamWeeklyMovements,
  requestUsersWeeklyMovements,
  requestWhereaboutsV2
} from '@features/whereabouts/services/Movements';
import { getTag } from '@shared/services/Tags';
import { TeamMovementResponse, WhereaboutsTeamViewResponse } from '@shared/models/api.models';
import { Tag } from '@shared/models/tags.models';
import {
  MemberMovements,
  sortWhereaboutsByDate,
  TagWithMovements,
  TeamWithMovements,
  UsersWhereaboutsDay
} from '@features/whereabouts/models/movements.models';
import { FOLLOWED_SCOPE, scopeKey, WhereaboutsScope } from '@features/whereabouts/models/whereaboutsScope';
import moment, { Moment } from "moment";

export interface TeamMovementsState {
  loading: boolean;
  visibleDate: Moment;
  // Which scope the arrays below were loaded for. Lets the reducers clear stale
  // groups when a page for a different scope mounts, and lets the follow picker
  // know not to overwrite a pinned team/workgroup page.
  scope: WhereaboutsScope;
  teamWhereabouts: TeamWithMovements[];
  workgroupWhereabouts: TagWithMovements[];
  currentUsersWhereabouts?: MemberMovements;
}

export const initialTeamMovementsState: TeamMovementsState = {
  loading: false,
  visibleDate: moment(0),
  scope: FOLLOWED_SCOPE,
  teamWhereabouts: [],
  workgroupWhereabouts: []
}

function toTeamWithMovements(tw: TeamMovementResponse): TeamWithMovements {
  return new TeamWithMovements(tw.teamEntity, true, true, (tw.teamWhereabouts ?? []).filter(Boolean), tw.userDesks, tw.restricted, tw.hasWeekNotes);
}

export const loadWhereabouts: any = createAsyncThunk(
  'teamMovements/loadWhereabouts',
  async (params: {calendarDate: Moment}, thunkAPI) => {
    const state = (thunkAPI.getState() as AppState);
    const calendarDate = params?.calendarDate ?? state.dashboard.calendarDate;

    const response: WhereaboutsTeamViewResponse = await requestWhereaboutsV2(calendarDate);
    const workgroups = (response?.workgroupsWhereabouts ?? []).filter(Boolean).map(ww => new TagWithMovements(ww.workGroup, true, true, (ww.whereabouts ?? []).filter(Boolean), undefined, ww.restricted));
    const teams = (response?.teamWhereabouts ?? []).filter(Boolean).map(toTeamWithMovements)

    return {
      teamWhereabouts: teams,
      workgroupWhereabouts: workgroups,
      visibleDate: calendarDate,
    }
  }
)

// Week view pinned to one team (company team page, search results page).
export const loadTeamScopedWhereabouts: any = createAsyncThunk(
  'teamMovements/loadTeamScopedWhereabouts',
  async (params: {teamId: string, calendarDate: Moment}, thunkAPI) => {
    const state = (thunkAPI.getState() as AppState);
    const calendarDate = params?.calendarDate ?? state.dashboard.calendarDate;

    const response: TeamMovementResponse = await requestTeamWeeklyMovements(params.teamId, calendarDate);

    return {
      teamWhereabouts: response ? [toTeamWithMovements(response)] : [],
      workgroupWhereabouts: [],
      visibleDate: calendarDate,
    }
  }
)

// Week view pinned to one workgroup (company workgroup page). The tag endpoint
// returns bare member rows, so the Tag itself is looked up from the loaded tag
// list where possible and fetched otherwise.
export const loadWorkgroupScopedWhereabouts: any = createAsyncThunk(
  'teamMovements/loadWorkgroupScopedWhereabouts',
  async (params: {workGroupId: number, calendarDate: Moment}, thunkAPI) => {
    const state = (thunkAPI.getState() as AppState);
    const calendarDate = params?.calendarDate ?? state.dashboard.calendarDate;

    const cachedTag = (state.userTags?.tags ?? []).find((t: Tag) => t.id === params.workGroupId);
    const tag: Tag = cachedTag ?? await getTag(params.workGroupId);
    const movements = await getMovementsByTags(calendarDate, [tag]);

    return {
      teamWhereabouts: [],
      workgroupWhereabouts: [new TagWithMovements(tag, true, true, (movements ?? []).filter(Boolean))],
      visibleDate: calendarDate,
    }
  }
)

export const loadCurrentUsersWhereabouts: any = createAsyncThunk(
  'teamMovements/loadCurrentUsersWhereabouts',
  async (params: {calendarDate: Moment}, thunkAPI) => {
    const state = (thunkAPI.getState() as AppState);
    const currentUser = state.auth.currentUser
    const calendarDate = params?.calendarDate ?? state.dashboard.calendarDate;


    if (currentUser) {
      const currentUsersWhereabouts = await requestUsersWeeklyMovements(calendarDate, currentUser?.id);
      return {
        currentUsersWhereabouts
      }
    } else {
      return {};
    }
  }
)

export const updateCurrentUsersWhereabouts: any = createAsyncThunk(
  'teamMovements/updateCurrentUsersWhereabouts',
  async (params: {date: string, whereaboutsToReplace: UsersWhereaboutsDay}, thunkAPI) => {
    const state = (thunkAPI.getState() as AppState);
    const memberWhereabouts = state.teamMovements.currentUsersWhereabouts
    const whereaboutsToUpdate = params.whereaboutsToReplace;

    if (!memberWhereabouts) {
      return;
    }

    const existingDays = memberWhereabouts.whereabouts ?? [];
    const dayAlreadyPresent = existingDays.some(w => w.date === whereaboutsToUpdate.date);

    // The weekly endpoint only returns days that already have a record, so a
    // previously-empty day has no entry to map over. Append it (and re-sort)
    // rather than dropping the update, matching updateTeamMembersWhereabouts.
    const updatedWhereaboutsDays = dayAlreadyPresent
      ? existingDays.map(w => w.date === whereaboutsToUpdate.date ? whereaboutsToUpdate : w)
      : [...existingDays, whereaboutsToUpdate].sort(sortWhereaboutsByDate);

    return {
      ...memberWhereabouts,
      whereabouts: updatedWhereaboutsDays,
    }
  }
)

// Replaces (or appends) one saved day on the matching member of a group list.
// Members who are not the saved user are returned untouched.
export function patchMembersWhereabouts(members: MemberMovements[] | undefined, whereaboutsToUpdate: UsersWhereaboutsDay): MemberMovements[] | undefined {
  if (!members) return members;
  const targetDate = whereaboutsToUpdate.date;
  return members.map(memberMovements => {
    if (!memberMovements || memberMovements.id !== whereaboutsToUpdate.userId) {
      return memberMovements;
    }
    const existing = memberMovements.whereabouts ?? [];
    if (existing.find(w => w.date === targetDate)) {
      return {
        ...memberMovements,
        whereabouts: existing.map(w => w.date === targetDate ? whereaboutsToUpdate : w)
      }
    }
    const updatedMemberWhereabouts = [...existing, whereaboutsToUpdate];
    updatedMemberWhereabouts.sort(sortWhereaboutsByDate);
    return {
      ...memberMovements,
      whereabouts: updatedMemberWhereabouts,
    }
  });
}

export const updateTeamMembersWhereabouts: any = createAsyncThunk(
  'teamMovements/updateTeamMembersWhereabouts',
  async (params: {whereaboutsToReplace: UsersWhereaboutsDay}, thunkAPI) => {
    const state = (thunkAPI.getState() as AppState);
    const whereaboutsToUpdate = params.whereaboutsToReplace;

    const teamWhereabouts = state.teamMovements.teamWhereabouts.map((team: TeamWithMovements) =>
      team.movements ? {...team, movements: patchMembersWhereabouts(team.movements, whereaboutsToUpdate)} : team);
    const workgroupWhereabouts = state.teamMovements.workgroupWhereabouts.map((workgroup: TagWithMovements) =>
      workgroup.movements ? {...workgroup, movements: patchMembersWhereabouts(workgroup.movements, whereaboutsToUpdate)} : workgroup);

    return { teamWhereabouts, workgroupWhereabouts };
  }
)

function scopeOfPendingAction(action: any): WhereaboutsScope {
  if (loadTeamScopedWhereabouts.pending.match(action)) {
    return { kind: 'team', teamId: action.meta.arg.teamId };
  }
  if (loadWorkgroupScopedWhereabouts.pending.match(action)) {
    return { kind: 'workgroup', workGroupId: action.meta.arg.workGroupId };
  }
  return FOLLOWED_SCOPE;
}

const teamMovementsSlice = createSlice({
  name: 'teamMovements',
  initialState: initialTeamMovementsState,
  reducers: {
    reset: () => initialTeamMovementsState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateCurrentUsersWhereabouts.fulfilled, (state, action) => ({...state, currentUsersWhereabouts: action.payload}))
      .addCase(updateTeamMembersWhereabouts.fulfilled, (state, action) => ({
        ...state,
        teamWhereabouts: action.payload.teamWhereabouts,
        workgroupWhereabouts: action.payload.workgroupWhereabouts,
      }))
      .addCase(loadCurrentUsersWhereabouts.pending, (state, action) => ({...state, currentUsersWhereabouts: undefined,}))
      .addCase(loadCurrentUsersWhereabouts.fulfilled, (state, action) => ({...state, currentUsersWhereabouts: action.payload.currentUsersWhereabouts,}))
      .addMatcher(isAnyOf(loadWhereabouts.pending, loadTeamScopedWhereabouts.pending, loadWorkgroupScopedWhereabouts.pending), (state, action) => {
        const incomingScope = scopeOfPendingAction(action);
        const scopeChanged = scopeKey(incomingScope) !== scopeKey(state.scope);
        return {
          ...state,
          loading: true,
          scope: incomingScope,
          // Never show one scope's groups under another scope's heading while
          // the new request is in flight.
          teamWhereabouts: scopeChanged ? [] : state.teamWhereabouts,
          workgroupWhereabouts: scopeChanged ? [] : state.workgroupWhereabouts,
        };
      })
      .addMatcher(isAnyOf(loadWhereabouts.rejected, loadTeamScopedWhereabouts.rejected, loadWorkgroupScopedWhereabouts.rejected), (state) => ({...state, loading: false,}))
      .addMatcher(isAnyOf(loadWhereabouts.fulfilled, loadTeamScopedWhereabouts.fulfilled, loadWorkgroupScopedWhereabouts.fulfilled), (state, action: any) => ({
        ...state,
        loading: false,
        teamWhereabouts: action.payload.teamWhereabouts,
        workgroupWhereabouts: action.payload.workgroupWhereabouts,
        visibleDate: action.payload.visibleDate,
      }));
  }
})

export default teamMovementsSlice.reducer;


// Selectors
const selectIsTeamMovementsLoading = (state: AppState) => state.teamMovements.loading;
export const selectWhereabouts = (state: AppState) => ({
  workgroups: state.teamMovements.workgroupWhereabouts,
  teams: state.teamMovements.teamWhereabouts,
})

export const selectCurrentUsersWhereabouts = (state: AppState) => state.teamMovements.currentUsersWhereabouts;
export const selectWhereaboutsScope = (state: AppState) => state.teamMovements.scope;
export const selectIsWhereaboutsLoading = selectIsTeamMovementsLoading;
